import { useCallback, useEffect, useMemo, useState } from "react";
import mark from "../assets/stellar-siege-mark.svg";
import { Button } from "../components/Button";
import { DailyOpsModal } from "../components/DailyOpsModal";
import { SettingsPanel } from "../components/SettingsPanel";
import { MAPS } from "../game/constants/maps";
import { claimDailyReward, ensureDailyOps, getDailyClaimables, getDailyDayKey } from "../services/firebase/dailyService";
import { getLeaderboardRefreshInterval, loadTopLeaderboardEntries } from "../services/firebase/leaderboardService";
import { logout } from "../services/firebase/authService";
import { checkUsernameAvailability, generateUniqueUsername, reserveUsername } from "../services/firebase/profileService";

const leaderboardModes = [
	{ id: "campaign", title: "Campaign Top 10" },
	{ id: "survival", title: "Survival Top 10" },
];

export function MainMenu({ controller, auth, settings, setSettings }) {
	const canPlay = controller.canPlay;
	const playDisabled = auth.loading || !canPlay;
	const [settingsOpen, setSettingsOpen] = useState(false);
	const [dailyOpsOpen, setDailyOpsOpen] = useState(false);
	const [menuView, setMenuView] = useState("home");
	const [usernameInput, setUsernameInput] = useState("");
	const [usernameStatus, setUsernameStatus] = useState({ tone: "muted", message: "" });
	const [savingUsername, setSavingUsername] = useState(false);
	const [syncingDaily, setSyncingDaily] = useState(false);
	const [claimingRewardId, setClaimingRewardId] = useState("");
	const [leaderboards, setLeaderboards] = useState({ campaign: [], survival: [] });
	const [leaderboardBusy, setLeaderboardBusy] = useState(true);
	const [leaderboardStamp, setLeaderboardStamp] = useState(null);

	const activeUsername = auth.profile?.username || "";
	const usernameChanged = usernameInput.trim() && usernameInput.trim() !== activeUsername;
	const leaderboardDay = useMemo(() => getDailyDayKey(), []);
	const daily = auth.profile?.daily || null;
	const commandMarks = auth.profile?.commandMarks || 0;
	const menuNotice = typeof controller.ui.notice?.message === "string" && controller.ui.notice.message.trim()
		? controller.ui.notice
		: null;

	useEffect(() => {
		setUsernameInput(activeUsername);
		setUsernameStatus({
			tone: activeUsername ? "success" : "muted",
			message: activeUsername ? "Username locked in and ready for the boards." : "Create or generate a unique username.",
		});
	}, [activeUsername]);

	useEffect(() => {
		if (!auth.user || !auth.profile || syncingDaily) return undefined;
		if (daily?.dayKey === leaderboardDay && daily?.version === 2 && Array.isArray(daily?.activeObjectives)) return undefined;

		let cancelled = false;
		setSyncingDaily(true);
		ensureDailyOps(auth.user, auth.profile)
			.then(async () => {
				if (!cancelled) await auth.refreshProfile?.();
			})
			.catch((error) => {
				console.error("Daily ops sync failed.", error);
			})
			.finally(() => {
				if (!cancelled) setSyncingDaily(false);
			});

		return () => {
			cancelled = true;
		};
	}, [auth.profile, auth.refreshProfile, auth.user, syncingDaily, daily?.activeObjectives, daily?.dayKey, daily?.version, leaderboardDay]);

	useEffect(() => {
		if (!auth.user || !auth.profile || usernameInput.trim() === activeUsername || !usernameInput.trim()) return undefined;

		let cancelled = false;
		setUsernameStatus({ tone: "muted", message: "Checking username..." });
		const timeout = window.setTimeout(() => {
			checkUsernameAvailability(usernameInput.trim(), auth.user.uid)
				.then((result) => {
					if (cancelled) return;
					setUsernameStatus(result.ok
						? { tone: "success", message: "Username available." }
						: { tone: "warn", message: result.reason });
				})
				.catch(() => {
					if (!cancelled) setUsernameStatus({ tone: "warn", message: "Could not verify username right now." });
				});
		}, 280);

		return () => {
			cancelled = true;
			window.clearTimeout(timeout);
		};
	}, [activeUsername, auth.profile, auth.user, usernameInput]);

	const refreshLeaderboards = useCallback(async () => {
		setLeaderboardBusy(true);
		try {
			const [campaign, survival] = await Promise.all([
				loadTopLeaderboardEntries("campaign"),
				loadTopLeaderboardEntries("survival"),
			]);
			setLeaderboards({ campaign, survival });
			setLeaderboardStamp(new Date());
		} catch (error) {
			console.error("Leaderboard refresh failed.", error);
		} finally {
			setLeaderboardBusy(false);
		}
	}, []);

	useEffect(() => {
		let cancelled = false;
		const guardedRefresh = async () => {
			if (cancelled) return;
			await refreshLeaderboards();
		};

		const handleLeaderboardUpdate = () => {
			window.setTimeout(() => {
				if (!cancelled) guardedRefresh();
			}, 300);
		};

		guardedRefresh();
		const interval = window.setInterval(guardedRefresh, getLeaderboardRefreshInterval());
		window.addEventListener("focus", guardedRefresh);
		window.addEventListener("stellar:leaderboard-updated", handleLeaderboardUpdate);

		return () => {
			cancelled = true;
			window.clearInterval(interval);
			window.removeEventListener("focus", guardedRefresh);
			window.removeEventListener("stellar:leaderboard-updated", handleLeaderboardUpdate);
		};
	}, [refreshLeaderboards]);

	const saveUsername = async () => {
		if (!auth.user || !auth.profile || !usernameInput.trim()) return;
		setSavingUsername(true);
		try {
			await reserveUsername({
				user: auth.user,
				username: usernameInput.trim(),
				previousProfile: auth.profile,
			});
			await auth.refreshProfile?.();
			setUsernameStatus({ tone: "success", message: "Username updated." });
		} catch (error) {
			setUsernameStatus({ tone: "warn", message: error.message || "Could not save that username." });
		} finally {
			setSavingUsername(false);
		}
	};

	const randomizeUsername = async () => {
		setSavingUsername(true);
		try {
			const generated = await generateUniqueUsername(auth.user?.uid || null);
			setUsernameInput(generated);
			setUsernameStatus({ tone: "success", message: "Generated a unique username." });
		} catch (error) {
			setUsernameStatus({ tone: "warn", message: error.message || "Could not generate a username right now." });
		} finally {
			setSavingUsername(false);
		}
	};

	const claimables = getDailyClaimables(daily);
	const activeObjectives = daily?.activeObjectives || [];
	const completedOps = daily?.completedObjectives || [];
	const readyCount = claimables.length;

	const claimOpsReward = async (rewardId) => {
		if (!auth.user || !rewardId || claimingRewardId) return;
		setClaimingRewardId(rewardId);
		try {
			await claimDailyReward({ user: auth.user, rewardId });
			await auth.refreshProfile?.();
		} catch (error) {
			console.error("Daily reward claim failed.", error);
		} finally {
			setClaimingRewardId("");
		}
	};

	const claimAllOpsRewards = async () => {
		if (!auth.user || !claimables.length || claimingRewardId) return;

		const rewardIds = claimables.map((claimable) => claimable.rewardId);
		setClaimingRewardId("bulk");
		try {
			for (const rewardId of rewardIds) {
				await claimDailyReward({ user: auth.user, rewardId });
			}
			await auth.refreshProfile?.();
		} catch (error) {
			console.error("Bulk daily reward claim failed.", error);
		} finally {
			setClaimingRewardId("");
		}
	};

	const updateSettings = async (nextSettings) => {
		const nextDifficulty = nextSettings?.difficultyMode;
		const previousDifficulty = settings?.difficultyMode;
		if (nextDifficulty && previousDifficulty && nextDifficulty !== previousDifficulty && controller.ui.hasSave) {
			const confirmed = window.confirm("Changing difficulty will delete your saved game. Continue?");
			if (!confirmed) return;
			const cleared = await controller.actions.clearSave();
			if (!cleared) return;
		}
		setSettings(nextSettings);
	};

	return (
		<main className="menu-screen">
			<section className="menu-hero">
				<button type="button" className="settings-gear" aria-label="Open settings" onClick={() => setSettingsOpen(true)}>
					{"\u2699"}
				</button>
				<img src={mark} alt="" className="menu-hero__mark" />
				<div>
					<div className="eyebrow">Tower Defense Command</div>
					<h1>Stellar Siege</h1>
					<p>Push your campaign score higher, hold the line in survival, and keep your commander identity climbing the daily boards.</p>
					<div className="menu-hero__meta">
						{auth.profile?.username && <div className="menu-commander-tag">Commander: {auth.profile.username}</div>}
						<div className="command-marks-badge">
							<small>Command Marks</small>
							<strong>{commandMarks}</strong>
						</div>
					</div>
				</div>
				<div className="menu-hero__actions">
					{menuView === "home" ? (
						<>
							<Button tone="success" onClick={controller.actions.startCampaign} disabled={playDisabled}>Begin Campaign</Button>
							<Button tone="warning" onClick={() => setMenuView("survival")} disabled={playDisabled}>Survival Mode</Button>
							<Button tone="primary" onClick={controller.actions.load} disabled={playDisabled || !controller.ui.hasSave || controller.busy.loadingSave}>
								{controller.busy.loadingSave ? "Loading" : "Resume Save"}
							</Button>
						</>
					) : (
						<>
							<Button tone="ghost" onClick={() => setMenuView("home")}>Back</Button>
							<div className="survival-hero-copy">Choose a map and defend it for as many waves as you can.</div>
						</>
					)}
				</div>
				{!auth.loading && !canPlay && (
					<div className="login-required">
						<strong>Login required</strong>
						<span>Create an account or sign in before starting a campaign.</span>
					</div>
				)}
				{menuNotice && <div className={`toast toast--${menuNotice.tone}`}>{menuNotice.message}</div>}
			</section>

			{menuView === "home" ? (
				<section className="menu-grid">
					<div className="panel daily-briefing">
						<div className="panel__heading">
							<div>
								<div className="eyebrow">Daily Ops Console</div>
								<h2>{leaderboardDay}</h2>
							</div>
							<small className="leaderboard-panel__stamp">Streak {daily?.loginStreak || 1} | Marks {commandMarks}</small>
						</div>
						<div className="daily-briefing__body">
							<div className="daily-briefing__stats">
								<div>
									<small>Ready To Claim</small>
									<strong>{readyCount}</strong>
								</div>
								<div>
									<small>Active Ops</small>
									<strong>{activeObjectives.length}</strong>
								</div>
								<div>
									<small>Claimed Today</small>
									<strong>{completedOps.length}</strong>
								</div>
							</div>
							<div className="daily-briefing__copy">
								<strong>{readyCount ? `${readyCount} reward${readyCount === 1 ? "" : "s"} ready for pickup` : "Command board is active"}</strong>
								<p>
									{readyCount
										? "Cash in ready ops, then the replaceable chains roll straight into their next tier."
										: "Open the Daily Ops board for the full list of rotating directives and tiered challenges."}
								</p>
							</div>
							<div className="daily-briefing__actions">
								<Button tone="primary" onClick={() => setDailyOpsOpen(true)}>Open Daily Ops</Button>
								{Boolean(readyCount) && (
									<Button tone="success" onClick={claimAllOpsRewards} disabled={Boolean(claimingRewardId)}>
										{claimingRewardId === "bulk" ? "Claiming..." : "Quick Claim"}
									</Button>
								)}
							</div>
						</div>
					</div>

					{leaderboardModes.map((mode) => (
						<div key={mode.id} className="panel leaderboard-panel">
							<div className="panel__heading">
								<div>
									<div className="eyebrow">Daily Leaderboard</div>
									<h2>{mode.title}</h2>
								</div>
								{leaderboardStamp && (
									<small className="leaderboard-panel__stamp">
										{leaderboardBusy ? "Refreshing..." : `Updated ${leaderboardStamp.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`}
									</small>
								)}
							</div>
							<div className="leaderboard-list" aria-live="polite">
								{leaderboards[mode.id].map((entry) => (
									<article key={`${mode.id}-${entry.id || entry.rank}`} className={`leaderboard-row ${entry.seeded ? "is-seeded" : ""}`}>
										<span className="leaderboard-row__rank">#{entry.rank}</span>
										<div className="leaderboard-row__name">
											<strong>{entry.username}</strong>
											<small>{mode.id === "campaign" ? `Map ${entry.mapIdx + 1}, Wave ${entry.wave}` : `Wave ${entry.wave}`}</small>
										</div>
										<div className="leaderboard-row__score">{entry.score}</div>
									</article>
								))}
								{!leaderboardBusy && !leaderboards[mode.id].length && (
									<div className="leaderboard-empty">No entries yet. The first commander run will light this board up.</div>
								)}
							</div>
						</div>
					))}
				</section>
			) : (
				<section className="menu-grid menu-grid--survival">
					<div className="panel survival-panel">
						<div className="panel__heading">
							<div>
								<div className="eyebrow">Survival Mode</div>
								<h2>Select A Sector</h2>
							</div>
						</div>
						<div className="survival-map-list">
							{MAPS.map((map, index) => (
								<button
									key={map.name}
									type="button"
									className="survival-map-card"
									style={{ "--tone": map.color }}
									onClick={() => controller.actions.startSurvival(index)}
									disabled={playDisabled}
								>
									<span>{index + 1}</span>
									<div>
										<strong>{map.name}</strong>
										<p>{map.desc}</p>
										<small>{map.threat}</small>
									</div>
								</button>
							))}
						</div>
					</div>
				</section>
			)}

			{dailyOpsOpen && (
				<DailyOpsModal
					dayKey={leaderboardDay}
					daily={daily}
					commandMarks={commandMarks}
					onClose={() => setDailyOpsOpen(false)}
					onClaim={claimOpsReward}
					busyRewardId={claimingRewardId}
				/>
			)}

			{settingsOpen && (
				<div className="settings-modal" role="dialog" aria-modal="true" aria-label="Settings">
					<div className="settings-modal__scrim" onClick={() => setSettingsOpen(false)} />
					<div className="settings-modal__panel">
						<SettingsPanel
							settings={settings}
							onChange={updateSettings}
							onClose={() => setSettingsOpen(false)}
							account={auth.user ? { onLogout: logout } : null}
							identity={auth.user ? {
								usernameInput,
								setUsernameInput,
								status: usernameStatus,
								onRandomize: randomizeUsername,
								onSave: saveUsername,
								saving: savingUsername,
								saveDisabled: !usernameChanged || savingUsername || auth.profileLoading,
								disabled: !auth.user || savingUsername || auth.profileLoading,
								commandMarks,
							} : null}
						/>
					</div>
				</div>
			)}
		</main>
	);
}
